import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { Text } from "ui/Typography"
import { Modal, Dropdown } from "antd"
import { EditOutlined, MoreOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { MEDIA_TYPES } from "../constants"
import styles from "./Message.module.css"
import Icon from "components/common/Icon"
import { setEditingMessage } from "store/conversations"
import DOMPurify from "dompurify"
import { htmlPreviewText } from "../utils"

export const MessageContent = ({ message, isOwnMessage }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)
  const [isVideoReady, setIsVideoReady] = useState(false)

  const handleMediaClick = (media) => {
    setSelectedMedia(media)
    setIsModalVisible(true)
  }

  const handleModalClose = () => {
    setIsModalVisible(false)
    setSelectedMedia(null)
  }

  const handleEditClick = (message) => {
    dispatch(setEditingMessage({ messageId: message?.id, messageBody: htmlPreviewText(message?.body) }))
  }

  useEffect(() => {
    setIsVideoReady(false)
  }, [message?.media_url])

  return (
    <>
      {/* Render media if it exists */}

      {message.media_type === MEDIA_TYPES.IMAGE && (
        <button
          type="button"
          onClick={() => handleMediaClick({ type: "image", url: message.media_url })}
          className={classNames(
            styles.imgMessage,
            "mb-xxs",
            styles.imgMessageHeight,
            !isOwnMessage && styles.mediaBackground,
            isOwnMessage && "self-end"
          )}
          style={{ all: "unset", cursor: "pointer", display: "block", padding: "0", border: "none" }}
          aria-label="View image"
        >
          <img
            src={message.media_url}
            alt="Message Media"
            style={{ width: "100%", display: "block", height: "100%", borderRadius: "10px" }}
          />
        </button>
      )}
      {message?.media_type === MEDIA_TYPES.VIDEO && (
        <button
          type="button"
          onClick={() => handleMediaClick({ type: "video", url: message.media_url })}
          className={classNames(
            styles.imgMessage,
            "mb-xxs",
            "position-relative",
            !isOwnMessage && isVideoReady && styles.mediaBackground,
            isOwnMessage && "self-end"
          )}
          style={{ all: "unset", cursor: "pointer", display: "block", padding: 0, border: "none", borderRadius: "10px" }}
          aria-label="View video"
        >
          <video width="100%" height="100%" src={message.media_url} preload="metadata" muted onLoadedData={() => setIsVideoReady(true)}>
            <track kind="captions" label="No captions" />
          </video>

          <Icon iconName="Play" className="position-absolute top-50 start-50 translate-middle text-white" width={30} height={30} />
        </button>
      )}
      <Modal open={isModalVisible} footer={null} onCancel={handleModalClose} centered width="50%" style={{ textAlign: "center" }}>
        {selectedMedia?.type === "image" ? (
          <img src={selectedMedia?.url} alt="" style={{ maxWidth: "100%", width: "100%", maxHeight: "80vh" }} />
        ) : (
          <video src={selectedMedia?.url} controls style={{ maxWidth: "100%", maxHeight: "80vh" }}>
            <track kind="captions" label="No captions" />
          </video>
        )}
      </Modal>

      {/* Render body if it exists */}
      {message?.body && (
        <Text
          variant="body"
          style={{ color: isOwnMessage ? "#ffffff" : undefined, whiteSpace: "pre-line", position: "relative" }}
          className={classNames(styles.senderMessage, { [styles.ownMessage]: isOwnMessage })}
        >
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message.body) }} />
          {!message.viewed && !message.failed && isOwnMessage && (
            <div className={styles.optionsWrapper}>
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "edit",
                      icon: <EditOutlined />,
                      label: t("global.edit"),
                      onClick: () => {
                        handleEditClick(message)
                      }
                    }
                  ]
                }}
                trigger={["click"]}
              >
                <span>
                  <MoreOutlined className={styles.dotsIcon} />
                </span>
              </Dropdown>
            </div>
          )}
        </Text>
      )}
    </>
  )
}

// Add prop type validation
MessageContent.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    media_type: PropTypes.string,
    media_url: PropTypes.string,
    body: PropTypes.string,
    viewed: PropTypes.bool,
    failed: PropTypes.bool
  }).isRequired,
  isOwnMessage: PropTypes.bool
}
